/**
 * computeNodeGroups - Computes node groups/clusters from network topology
 *
 * Useful when nodes lack group/cluster information. Can compute groups using:
 * - 'connected': Connected component membership
 * - 'degree': Degree-based binning (low/medium/high connectivity)
 * - custom function
 *
 * @param {Object} network - { nodes, links } network data
 * @param {Object} options - Configuration options
 * @param {string|function} [options.method='connected'] - Grouping method
 * @param {string} [options.groupProperty='group'] - Property name to add
 * @param {function} [options.nodeId] - Extract ID from node (default: d => d.id)
 * @param {function} [options.linkSource] - Extract source from link (default: d => d.source)
 * @param {function} [options.linkTarget] - Extract target from link (default: d => d.target)
 * @returns {Object} { nodes, links } with group property added to nodes
 *
 * @example
 * // Group by connected components
 * const grouped = computeNodeGroups(data);
 * // => nodes now have { id: 'A', group: 0 }, { id: 'B', group: 0 }, { id: 'C', group: 1 }
 *
 * @example
 * // Group by node degree
 * const grouped = computeNodeGroups(data, { method: 'degree' });
 *
 * @example
 * // Custom grouping function
 * const grouped = computeNodeGroups(data, {
 *   method: (node, index, nodes, links, degrees) => {
 *     return degrees.get(node.id) > 5 ? 'hub' : 'leaf';
 *   }
 * });
 */
export function computeNodeGroups(
  network,
  {
    method = "connected",
    groupProperty = "group",
    nodeId = (d) => d.id,
    linkSource = (d) => (typeof d.source === "object" ? d.source.id : d.source),
    linkTarget = (d) => (typeof d.target === "object" ? d.target.id : d.target),
  } = {}
) {
  if (!network || !network.nodes) {
    throw new Error("computeNodeGroups: network must have a nodes array");
  }

  const { nodes, links = [] } = network;

  // Build node ID list and index map
  const nodeIds = nodes.map(nodeId);
  const idToIndex = new Map(nodeIds.map((id, i) => [id, i]));

  // Compute degrees (useful for multiple methods)
  const degrees = new Map(nodeIds.map((id) => [id, 0]));
  for (const link of links) {
    const src = linkSource(link);
    const tgt = linkTarget(link);
    if (degrees.has(src)) degrees.set(src, degrees.get(src) + 1);
    if (degrees.has(tgt)) degrees.set(tgt, degrees.get(tgt) + 1);
  }

  let groupFn;

  if (typeof method === "function") {
    // Custom function receives node, index, all nodes, all links, and degrees map
    groupFn = (node, i) => method(node, i, nodes, links, degrees);
  } else if (method === "connected") {
    // Union-Find for connected components
    const parent = nodeIds.map((_, i) => i);
    const find = (i) => (parent[i] === i ? i : (parent[i] = find(parent[i])));
    const union = (a, b) => {
      parent[find(a)] = find(b);
    };

    for (const link of links) {
      const si = idToIndex.get(linkSource(link));
      const ti = idToIndex.get(linkTarget(link));
      if (si !== undefined && ti !== undefined) union(si, ti);
    }

    // Normalize component IDs to 0, 1, 2...
    const componentMap = new Map();
    let nextGroup = 0;
    groupFn = (node, i) => {
      const root = find(i);
      if (!componentMap.has(root)) {
        componentMap.set(root, nextGroup++);
      }
      return componentMap.get(root);
    };
  } else if (method === "degree") {
    // Bin nodes by degree into low/medium/high
    const maxDegree = Math.max(...degrees.values(), 1);
    groupFn = (node) => {
      const degree = degrees.get(nodeId(node)) || 0;
      // Create 4 bins: 0, 1, 2, 3
      return Math.min(3, Math.floor((degree / maxDegree) * 4));
    };
  } else {
    throw new Error(
      `computeNodeGroups: unknown method "${method}". Use "connected", "degree", or a function.`
    );
  }

  const groupedNodes = nodes.map((node, i) => ({
    ...node,
    [groupProperty]: groupFn(node, i),
  }));

  return { nodes: groupedNodes, links: [...links] };
}
